import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { StarRating } from "./StarRating";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, AlertTriangle } from "lucide-react";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const [feedbackType, setFeedbackType] = useState<"experience" | "problem">(
    "experience"
  );
  const [generalExperience, setGeneralExperience] = useState(0);
  const [easeOfUse, setEaseOfUse] = useState(0);
  const [featureSatisfaction, setFeatureSatisfaction] = useState(0);
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // New states for problem reporting
  const [problemType, setProblemType] = useState("");
  const [problemDescription, setProblemDescription] = useState("");
  const [expectedOutcome, setExpectedOutcome] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [followUpEmail, setFollowUpEmail] = useState(user?.email || "");

  const resetForm = () => {
    setGeneralExperience(0);
    setEaseOfUse(0);
    setFeatureSatisfaction(0);
    setComments("");
    // Reset problem specific fields
    setProblemType("");
    setProblemDescription("");
    setExpectedOutcome("");
    setStepsToReproduce("");
    setFollowUpEmail(user?.email || "");
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "Debes iniciar sesión para enviar feedback.",
        variant: "destructive",
      });
      return;
    }

    if (feedbackType === "experience" && generalExperience === 0) {
      toast({
        title: "Error",
        description: "Por favor, califica tu experiencia general.",
        variant: "destructive",
      });
      return;
    }

    // Validate problem fields if feedbackType is "problem"
    if (
      feedbackType === "problem" &&
      (!problemType || !problemDescription || !expectedOutcome)
    ) {
      toast({
        title: "Error",
        description:
          "Por favor, completa todos los campos requeridos para reportar un problema.",
        variant: "destructive",
      });
      return;
    }

    if (!comments.trim()) {
      toast({
        title: "Error",
        description: "Por favor, añade un comentario.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("feedback").insert({
        user_id: user.id,
        feedback_type:
          feedbackType === "experience"
            ? "Experiencia de uso"
            : "Reportar problema",
        general_experience:
          feedbackType === "experience" ? generalExperience : null,
        ease_of_use: feedbackType === "experience" ? easeOfUse : null,
        feature_satisfaction:
          feedbackType === "experience" ? featureSatisfaction : null,
        comments,
        // Add problem specific fields
        problem_type: feedbackType === "problem" ? problemType : null,
        problem_description:
          feedbackType === "problem" ? problemDescription : null,
        expected_outcome: feedbackType === "problem" ? expectedOutcome : null,
        steps_to_reproduce:
          feedbackType === "problem" ? stepsToReproduce : null,
        follow_up_email: feedbackType === "problem" ? followUpEmail : null,
      });

      if (error) throw error;

      toast({
        title: "Gracias por tu feedback!",
        description:
          "Hemos recibido tu experiencia. ¡Ayuda a mejorar la aplicación!",
      });
      resetForm();
      onClose();
    } catch (error) {
      console.error("Error submitting feedback:", error);
      toast({
        title: "Error",
        description: "Hubo un error al enviar tu feedback. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {feedbackType === "experience" ? (
              <MessageSquare className="h-5 w-5 text-blue-500" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            )}
            Comparte tu Experiencia
          </DialogTitle>
          <DialogDescription>
            Ayúdanos a mejorar contándonos sobre tu experiencia con la
            aplicación.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <RadioGroup
            defaultValue="experience"
            value={feedbackType}
            onValueChange={(value: "experience" | "problem") => {
              setFeedbackType(value);
              resetForm(); // Reset ratings when switching type
            }}
            className="flex space-x-4 mb-4">
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="experience" id="experience" />
              <Label htmlFor="experience">Experiencia de uso</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="problem" id="problem" />
              <Label htmlFor="problem">Reportar problema</Label>
            </div>
          </RadioGroup>

          {feedbackType === "experience" && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold">
                Califica tu experiencia
              </h3>
              <div>
                <Label htmlFor="generalExperience" className="mb-2 block">
                  Experiencia general con la aplicación
                </Label>
                <StarRating
                  rating={generalExperience}
                  onRatingChange={setGeneralExperience}
                />
              </div>
              <div>
                <Label htmlFor="easeOfUse" className="mb-2 block">
                  Facilidad de uso
                </Label>
                <StarRating rating={easeOfUse} onRatingChange={setEaseOfUse} />
              </div>
              <div>
                <Label htmlFor="featureSatisfaction" className="mb-2 block">
                  Satisfacción con las funcionalidades
                </Label>
                <StarRating
                  rating={featureSatisfaction}
                  onRatingChange={setFeatureSatisfaction}
                />
              </div>
            </div>
          )}

          {feedbackType === "problem" && (
            <div className="space-y-4">
              <h3 className="text-base font-semibold">Detalles del problema</h3>
              <div>
                <Label htmlFor="problemType" className="mb-2 block">
                  Tipo de problema
                </Label>
                <Select onValueChange={setProblemType} value={problemType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona el tipo de problema" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">Error (Bug)</SelectItem>
                    <SelectItem value="feature_request">
                      Solicitud de Característica
                    </SelectItem>
                    <SelectItem value="usability_issue">
                      Problema de Usabilidad
                    </SelectItem>
                    <SelectItem value="performance_issue">
                      Problema de Rendimiento
                    </SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="problemDescription" className="mb-2 block">
                  Descripción del problema
                </Label>
                <Textarea
                  id="problemDescription"
                  placeholder="Describe detalladamente qué problema estás experimentando..."
                  value={problemDescription}
                  onChange={(e) => setProblemDescription(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <div>
                <Label htmlFor="expectedOutcome" className="mb-2 block">
                  ¿Qué esperabas que pasara?
                </Label>
                <Textarea
                  id="expectedOutcome"
                  placeholder="Describe qué resultado esperabas obtener..."
                  value={expectedOutcome}
                  onChange={(e) => setExpectedOutcome(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <div>
                <Label htmlFor="stepsToReproduce" className="mb-2 block">
                  Pasos para reproducir (opcional)
                </Label>
                <Textarea
                  id="stepsToReproduce"
                  placeholder="1. Hice clic en...\n2. Luego seleccioné...\n3. El error apareció cuando..."
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <div>
                <Label htmlFor="followUpEmail" className="mb-2 block">
                  Email para seguimiento (opcional)
                </Label>
                <Input
                  id="followUpEmail"
                  type="email"
                  placeholder="tu.email@ejemplo.com"
                  value={followUpEmail}
                  onChange={(e) => setFollowUpEmail(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="comments">Cuéntanos sobre tu experiencia</Label>
            <Textarea
              id="comments"
              placeholder="Describe qué te gusta, qué no te gusta, o qué cambiarías..."
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Enviando..." : "Enviar Feedback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
